import { EyeOutlined } from '@ant-design/icons'
import { Typography } from '@vivaldis/antd-ui'
import { WorkPostSheetItemTypesEnum } from '@vivaldis/graphql'
import { format, isValid, parseISO } from 'date-fns'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from './components/Row'
import { Section } from './components/Section'
import {
  DATE_ITEMS,
  DATES_OF_ADVICE_ITEMS,
  HEALTH_RISKS_ITEMS,
  MATERNITY_PROTECTION_MEASURES_ITEMS,
  REQUIRED_VACCINATIONS_AND_TESTS_ITEMS,
  TEXT_ITEMS,
  WORK_CLOTHES_AND_PROTECTIVE_EQUIPMENT_ITEMS
} from './constants/items'
import type { WorkPostSheet } from './typings/WorkPostSheet'

export interface ViewWorkPostSheetScreenContentProps {
  workPostSheet: WorkPostSheet
}

// Read-only work post sheet: the same UI as the overview step of WorkPostSheetForm.
export const ViewWorkPostSheetScreenContent: FC<
  ViewWorkPostSheetScreenContentProps
> = ({ workPostSheet }) => {
  const [t, i18n] = useTranslation('web_ui')

  const items = useMemo(() => workPostSheet.items || [], [workPostSheet.items])
  const emptyValue = t('view_work_post_sheet_screen_content.empty')

  const getItemLabel = useCallback(
    (name: WorkPostSheetItemTypesEnum) => {
      const translationKey = `view_work_post_sheet_screen_content.${name.toLocaleLowerCase()}`
      const label = t(`${translationKey}.label`)
      return i18n.exists(`${translationKey}.code`, { ns: 'web_ui' })
        ? `${label} — ${t(`${translationKey}.code`)}`
        : label
    },
    [i18n, t]
  )

  // a checkbox item is checked when it's in the list, date and text items are set by their comment
  const getItemValue = useCallback(
    (name: WorkPostSheetItemTypesEnum): string => {
      const item = items.find(item => item.name === name)
      const comment = item?.comment

      if (DATE_ITEMS.includes(name)) {
        const date = comment ? parseISO(comment) : null
        return date && isValid(date) ? format(date, 'dd/MM/yyyy') : emptyValue
      }
      if (TEXT_ITEMS.includes(name)) {
        return comment || emptyValue
      }
      if (!item) {
        return t('view_work_post_sheet_screen_content.no')
      }
      return comment || t('view_work_post_sheet_screen_content.yes')
    },
    [emptyValue, items, t]
  )

  const renderItems = (names: WorkPostSheetItemTypesEnum[]) =>
    names.map(name => (
      <Row key={name} label={getItemLabel(name)} value={getItemValue(name)} />
    ))

  const pdfUrl = items.find(
    item => item.name === WorkPostSheetItemTypesEnum.PDF_URL
  )?.comment

  if (pdfUrl) {
    return (
      <Section>
        <Row
          label={t('view_work_post_sheet_screen_content.custom_document.label')}
          value={t('view_work_post_sheet_screen_content.custom_document.value')}
        />
        <Typography.Link href={pdfUrl} target="_blank" strong>
          <EyeOutlined style={{ marginRight: 8 }} />
          {t('view_work_post_sheet_screen_content.custom_document.view_pdf')}
        </Typography.Link>
      </Section>
    )
  }

  const maternityProtectionMeasuresRequired = items.some(
    item =>
      MATERNITY_PROTECTION_MEASURES_ITEMS.includes(item.name) && !!item.comment
  )

  return (
    <>
      <Section>
        <Row
          label={t('view_work_post_sheet_screen_content.tasksToPerform.label')}
          value={workPostSheet.tasksToPerform || emptyValue}
        />
        <Row
          label={t(
            'view_work_post_sheet_screen_content.requiredQualifications.label'
          )}
          value={workPostSheet.requiredQualifications || emptyValue}
        />
        <Row
          label={t('view_work_post_sheet_screen_content.workEquipment.label')}
          value={workPostSheet.workEquipment || emptyValue}
        />
        <Row
          label={t(
            'view_work_post_sheet_screen_content.priorInstructions.label'
          )}
          value={workPostSheet.priorInstructions || emptyValue}
        />
        <Row
          label={t(
            'view_work_post_sheet_screen_content.foreseeableTraining.label'
          )}
          value={workPostSheet.foreseeableTraining || emptyValue}
        />
        <Row
          label={t(
            'view_work_post_sheet_screen_content.workstationLocation.label'
          )}
          value={workPostSheet.workstationLocation || emptyValue}
        />
        <Row
          label={t('view_work_post_sheet_screen_content.welcomeOfficer.label')}
          value={workPostSheet.welcomeOfficer || emptyValue}
        />
        <Row
          label={t(
            'view_work_post_sheet_screen_content.welcomeOfficerPosition.label'
          )}
          value={workPostSheet.welcomeOfficerPosition || emptyValue}
        />
      </Section>

      <Section title={t('view_work_post_sheet_screen_content.group_1.label')}>
        {renderItems(WORK_CLOTHES_AND_PROTECTIVE_EQUIPMENT_ITEMS)}
      </Section>

      <Section title={t('view_work_post_sheet_screen_content.group_2.label')}>
        {renderItems(REQUIRED_VACCINATIONS_AND_TESTS_ITEMS)}
      </Section>

      <Section title={t('view_work_post_sheet_screen_content.group_3.label')}>
        {renderItems(HEALTH_RISKS_ITEMS)}
        <Row
          label={t('view_work_post_sheet_screen_content.group_4.label')}
          value={
            maternityProtectionMeasuresRequired
              ? t('view_work_post_sheet_screen_content.yes')
              : t('view_work_post_sheet_screen_content.no')
          }
        />
        {maternityProtectionMeasuresRequired
          ? renderItems(MATERNITY_PROTECTION_MEASURES_ITEMS)
          : null}
      </Section>

      <Section title={t('view_work_post_sheet_screen_content.group_5.label')}>
        {renderItems(DATES_OF_ADVICE_ITEMS)}
      </Section>
    </>
  )
}
